import { FC, RefObject, useEffect } from 'react';
import classNames from 'classnames';
import { asText } from '@prismicio/helpers';

import {
  Container,
  Heading,
  Link,
  PrismicImageWithAlt,
  useData,
} from '@/components';
import { useActiveHeaderSlice, useIsServerSide } from '@/hooks';
import { NavigationViewport } from '@/utils';

import styles from './megamenucustom.module.scss';

interface IMegaMenuCustomProps {
  itemID: string;
  className?: string;
  navbarRef: RefObject<HTMLElement | null>;
  id: string;
  anchorElement?: HTMLElement | null;
  viewport?: NavigationViewport;
}

const findMegaNavLink = (
  navigation: ReturnType<typeof useData>['navigation'],
  itemID: string,
) =>
  navigation?.data?.links?.find(
    (item: { external_link: { uid: string; type: string } }) => {
      const { uid, type } = item.external_link;
      return uid === itemID && type === 'custom_mega_menu';
    },
  );

export const isSingleMegaMenu = (
  navigation: ReturnType<typeof useData>['navigation'],
  itemID: string,
) => {
  const megaNav = findMegaNavLink(navigation, itemID);
  const body = megaNav?.external_link?.data?.slices;
  return body?.length === 1 && body?.[0]?.slice_type === 'group_link';
};

const MegaMenuCustom: FC<IMegaMenuCustomProps> = ({
  itemID,
  className,
  navbarRef,
  id,
  anchorElement,
  viewport = 'desktop',
}) => {
  const { navigation } = useData();
  const { isServerSide } = useIsServerSide();
  const activeSlice = useActiveHeaderSlice();

  const applyBorder =
    activeSlice?.variation === 'withCenteredLogoAndLocatorFix';

  useEffect(() => {
    const handleUpdate = () => {
      if (!navbarRef?.current) return;

      const navbarBottom = navbarRef.current.getBoundingClientRect().bottom;

      document.documentElement.style.setProperty(
        '--megamenu-top',
        `${navbarBottom}px`,
      );
    };

    const timer = setTimeout(handleUpdate, 100);

    window.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [navbarRef]);

  const megaNav: any = findMegaNavLink(navigation, itemID);
  const body = megaNav?.external_link?.data?.slices;
  const isSingle = body?.length === 1 && body?.[0]?.slice_type === 'group_link';

  useEffect(() => {
    if (!isSingle || !anchorElement) return;

    const handleUpdate = () => {
      const rect = anchorElement.getBoundingClientRect();

      document.documentElement.style.setProperty(
        '--megamenu-single-top',
        `${rect.bottom}px`,
      );
      document.documentElement.style.setProperty(
        '--megamenu-single-left',
        `${rect.left}px`,
      );
    };

    handleUpdate();

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [anchorElement, isSingle]);

  if (!megaNav) {
    // Keep a stable id target for aria-controls even when content is unavailable.
    return <div aria-hidden="true" className={className} hidden id={id} />;
  }

  if (isSingle) {
    const groupLink = body?.[0]?.items;

    return (
      <div
        className={classNames(
          styles.megamenu_single,
          {
            [styles.megamenu_single__anchored]: !!anchorElement,
          },
          className,
        )}
        data-type="single"
        id={id}
      >
        <ul className={styles.megamenu_single__content}>
          {groupLink.map((link, index) => (
            <li
              className={classNames(styles.megamenu_content__link)}
              key={index}
            >
              <Link aria-label={`Go to ${link?.label}`} href={link?.link}>
                {link?.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const groupsPictures = (
    body?.filter(item => item?.slice_type === 'group_picture') || []
  ).slice(0, 2);

  const groupsLinks = (
    body?.filter(item => item?.slice_type === 'group_link') || []
  ).slice(0, groupsPictures.length === 2 ? 1 : 3);

  return (
    <aside
      aria-labelledby={isServerSide ? undefined : `${id}-heading`}
      className={classNames(styles.megamenu, className, {
        [styles.megamenu__border_with_spacing]: applyBorder && !isSingle,
        [styles.megamenu_mobile]: viewport === 'mobile',
      })}
      data-type="mega"
      id={id}
    >
      {!isServerSide && (
        <Container
          className={classNames(styles.megamenu__container, {
            [styles.megamenu__container__doble_pictures_section]:
              groupsPictures.length === 2,
          })}
        >
          {groupsLinks.map((item, index) => (
            <div key={item.id} style={{ gridArea: `group-link${index}` }}>
              <Heading
                className={styles.title}
                id={index === 0 ? `${id}-heading` : undefined}
                level={6}
              >
                {asText(item?.primary?.group_link_label)}
              </Heading>
              <ul className={styles.links_content}>
                {item?.items?.map((link, index) => (
                  <li
                    className={classNames(styles.megamenu_content__link)}
                    key={index}
                  >
                    <Link
                      aria-label={
                        link?.label ? `Go to ${link?.label}` : undefined
                      }
                      href={link?.link}
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {groupsPictures.map((item, index) => (
            <div
              className={styles.pictures}
              key={item.id}
              style={{ gridArea: `group-picture${index}` }}
            >
              <Heading className={styles.title} level={6}>
                {item?.primary?.title}
              </Heading>
              <div className={styles.pictures_content}>
                {item?.items?.map((picture, index) => (
                  <Link
                    aria-label={
                      picture?.picture?.alt?.trim() ||
                      `Promotional image ${index + 1}`
                    }
                    className={styles.pictures_content__link}
                    href={picture?.link}
                    key={index}
                  >
                    <figure className={styles.pictures_content__figure}>
                      <PrismicImageWithAlt
                        defaultAlt="Brand logo"
                        field={picture?.picture}
                        height={picture?.picture?.dimensions?.height}
                        width={picture?.picture?.dimensions?.width}
                      />
                    </figure>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </Container>
      )}
    </aside>
  );
};

export default MegaMenuCustom;
